import { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import type { ReactElement } from 'react';
import { useQuerySubscription } from 'react-datocms';

import { PageLayout } from '@/components/layouts/page-layout';
import { PostListingView } from '@/components/views/posts/listing';
import { POST_TYPES, SITE_NAME } from '@/lib/constants';
import { AllPostsPostInterface, DatoQueryListenerOptions, PostInterface } from '@/types/cms-types';
import { getPostsSchema } from '@/utils/helpers/json-ld';
import { getPostPaginationPaths, getSharedStaticProps } from '@/utils/posts';

interface PageProps {
  subscription: DatoQueryListenerOptions;
  posts: AllPostsPostInterface[];
  postType: PostInterface['postType'];
  totalPosts: number;
  currentPage: number;
  canonical: string;
}

const POSTS_PER_PAGE = 12;

const Page = ({ subscription, posts, postType, totalPosts, currentPage, canonical }: PageProps): ReactElement => {
  const {
    data: { page },
    status,
    error,
  } = useQuerySubscription(subscription);

  const postsJsonLd = getPostsSchema(posts);

  return (
    <PageLayout
      metaTags={page?.seoMetaTags}
      previewStatus={{ status, error }}
      headerVariant="light"
      canonical={canonical}
      posts={page?.posts}
    >
      <Head>
        <title>Blog - News & Views - {SITE_NAME}</title>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: `${JSON.stringify(postsJsonLd)}`,
          }}
        />
      </Head>
      <PostListingView
        posts={posts}
        postType={postType}
        totalPosts={totalPosts}
        currentPage={currentPage}
        blocks={page?.blocks}
      />
    </PageLayout>
  );
};

export const getStaticProps: GetStaticProps = async (context) => {
  const currentPage = context.params?.page ? parseInt(context.params.page as string, 10) : 1;
  return getSharedStaticProps(POST_TYPES.BLOG, context, { page: currentPage, perPage: POSTS_PER_PAGE });
};

export const getStaticPaths: GetStaticPaths = async () => {
  return getPostPaginationPaths({ postType: POST_TYPES.BLOG, postsPerPage: POSTS_PER_PAGE });
};

export default Page;
